import { useState, memo } from 'react'

// Components
import { Hero } from 'kit'
import { Button, IconPlayTransparent, Pill } from '@tracklib/kit'
import VideoModal from 'components/modal/video'

// Hooks
import { useTracking } from 'modules/tracking/hooks'

// Utils
import { getDocumentUrl } from 'utils'

import styles from './frontpageheroblock.css'

type FrontPageHeroBlockProps = {
  value: {
    title: string
    description: string
    invite_key_copy_title: string
    invite_key_copy_description: string
    play_text: string
    background_image: number
    background_image_mobile: number
    background_image_background_color: string
    bg_video_webm?: string
    bg_video_mp4?: string
    bg_video_mobile_webm?: string
    bg_video_mobile_mp4?: string
    video_url: string
    button_url: string
    button_text: string
    margin_bottom?: number
    margin_bottom_mobile?: number
    size: number
    label?: string
  }
}

const FrontPageHeroBlock = ({
  value: {
    title,
    description,
    play_text,
    background_image,
    background_image_mobile,
    bg_video_webm,
    bg_video_mp4,
    bg_video_mobile_webm,
    bg_video_mobile_mp4,
    background_image_background_color,
    video_url,
    button_url,
    button_text,
    margin_bottom,
    margin_bottom_mobile,
    size,
    label,
  },
}: FrontPageHeroBlockProps): JSX.Element => {
  const trackEvent = useTracking()
  const [modalIsOpen, setModalOpen] = useState(false)

  const openModal = () => {
    setModalOpen(true)
    trackEvent('Play hero video')
  }

  return (
    <Hero
      image={background_image}
      mobileImageOverride={background_image_mobile}
      video={getDocumentUrl(bg_video_webm)}
      videoMp4={getDocumentUrl(bg_video_mp4)}
      mobileVideoOverride={getDocumentUrl(bg_video_mobile_webm)}
      mobileVideoOverrideMp4={getDocumentUrl(bg_video_mobile_mp4)}
      backgroundColor={background_image_background_color}
      marginBottom={margin_bottom}
      marginBottomMobile={margin_bottom_mobile}
      large={+size === 2 || !size}
      small={+size === 0}
      full={+size === 3}
    >
      <div className={styles.mainSplashContent}>
        <div className={styles.infoContainer} datatesting="heroblock_info">
          {label && (
            <Pill type="opaque" className={styles.pill}>
              {label}
            </Pill>
          )}
          <h1 className={label ? styles.smallTitle : styles.title} datatesting="heroblock_title">
            {title}
          </h1>
          {description && <span className={styles.description} dangerouslySetInnerHTML={{ __html: description }} />}
          {(button_url || video_url) && (
            <div className={styles.buttons}>
              {button_text && (
                <Button
                  variant="white"
                  size="large"
                  className={styles.signupButton}
                  to={button_url}
                  datatesting="frontpageherobutton"
                  onClick={() => trackEvent('Click hero button')}
                >
                  {button_text}
                </Button>
              )}
              {video_url && (
                <Button
                  variant={['transparent', 'white']}
                  size="large"
                  className={styles.playButtonContainer}
                  onClick={openModal}
                  datatesting="heroblock_playbutton"
                >
                  <IconPlayTransparent />
                  {play_text}
                </Button>
              )}
            </div>
          )}
          {!label && <div className={styles.spacer} />}
        </div>

        {video_url && <VideoModal videoUrl={video_url} isOpen={modalIsOpen} closeModal={() => setModalOpen(false)} />}
      </div>
    </Hero>
  )
}

export default memo<FrontPageHeroBlockProps>(FrontPageHeroBlock)
